import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportsApi, adminApi } from '../services/api';
import { useAuthStore } from '../context/auth.store';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import ReportAnalytics from '../components/ReportAnalytics';

const Icon = {
  Back:    () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  Refresh: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>,
  Search:  (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Sheet:   () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x="3" y1="9" x2="21" y2="9"/><line x1="9" y1="9" x2="9" y2="21"/></svg>,
  Sort:    (p) => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...p}><polyline points="6 9 12 3 18 9"/><polyline points="6 15 12 21 18 15"/></svg>,
  ZoomIn:  () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>,
  ZoomOut: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>,
  Menu:    () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
};

const FILE_EMOJI = { xlsx:'📊', xls:'📊', xlsm:'📊', csv:'📋', pdf:'📄' };

// ── Cell formatter ────────────────────────────────────────────────────────────
function fmtCell(val, colType) {
  if (val === '' || val === null || val === undefined) return { display:'—', cls:'cell-empty' };
  const s = String(val);
  if (colType === 'number') {
    const n = parseFloat(s.replace(/[,$%]/g, ''));
    if (!isNaN(n)) return { display: n.toLocaleString('en-IN'), cls:`cell-num ${n < 0 ? 'cell-num-negative' : ''}`.trim() };
  }
  if (colType === 'currency') {
    const n = parseFloat(s.replace(/[₹$€£,]/g, ''));
    if (!isNaN(n)) return { display: (n < 0 ? '-₹' : '₹') + Math.abs(n).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}), cls:`cell-currency ${n < 0 ? 'cell-num-negative' : ''}`.trim() };
  }
  if (colType === 'date') return { display:s, cls:'cell-date' };
  const lower = s.toLowerCase();
  if (['pending','open','new','in progress'].includes(lower))                        return { display:s, cls:'cell-status cell-status-warn' };
  if (['closed','done','completed','approved','resolved','paid'].includes(lower))    return { display:s, cls:'cell-status cell-status-ok' };
  if (['rejected','failed','cancelled','denied','overdue'].includes(lower))          return { display:s, cls:'cell-status cell-status-bad' };
  return { display:s, cls:'cell-text' };
}

const formatDateTime = (dateStr) => {
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return dateStr;
  const day = String(dt.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[dt.getMonth()];
  const year = dt.getFullYear();
  let hours = dt.getHours();
  const minutes = String(dt.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${day}-${month}-${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
};

// ── Remarks Cell component ───────────────────────────────────────────────────
function RemarksCell({ remarks, onAdd, userRole, allowedRoles, isVendorType }) {
  const [adding, setAdding] = useState(false);
  const [statusMode, setStatusMode] = useState(null); // 'REJECTED' or null
  const [text, setText] = useState('');
  const canAdd = allowedRoles.includes(userRole);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAdd(text, statusMode);
    setText('');
    setAdding(false);
    setStatusMode(null);
  };

  const handleApprove = () => {
    onAdd("Approved", "APPROVED");
  };

  const handleRejectClick = () => {
    setStatusMode("REJECTED");
    setAdding(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 320, fontSize: 12 }}>
      {remarks.map(r => (
        <div key={r.id} style={{ padding: '8px 10px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 4, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', fontSize: 10, gap: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#1E293B', fontWeight: 700 }}>{r.userName}</span>
              {r.status && (
                <span style={{ 
                  fontSize: 9, padding: '1px 5px', borderRadius: 4, fontWeight: 700,
                  background: r.status === 'APPROVED' ? '#EBFDF5' : '#FEF2F2',
                  color: r.status === 'APPROVED' ? '#10B981' : '#EF4444',
                  border: '1px solid',
                  borderColor: r.status === 'APPROVED' ? '#A7F3D0' : '#FCA5A5'
                }}>
                  {r.status}
                </span>
              )}
            </div>
            <span style={{ color: '#64748B', fontWeight: 500 }}>{formatDateTime(r.createdAt)}</span>
          </div>
          <div style={{ color: '#334155', fontSize: 12, fontWeight: 500, wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.4, marginTop: 2 }}>{r.remark}</div>
        </div>
      ))}

      {canAdd && (
        adding ? (
          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <input 
              value={text} 
              onChange={e => setText(e.target.value)} 
              placeholder={statusMode === 'REJECTED' ? "Reason for rejection..." : "Add remark..."} 
              style={{ flex: 1, padding: '6px 10px', fontSize: 12, border: `1px solid ${statusMode === 'REJECTED' ? '#EF4444' : 'var(--accent)'}`, borderRadius: 6, outline: 'none', background: 'white', color: '#1E293B' }}
              autoFocus
            />
            <button type="submit" style={{ padding: '2px 8px', border: 'none', background: statusMode === 'REJECTED' ? '#EF4444' : 'var(--accent)', color: 'white', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>✓</button>
            <button type="button" onClick={() => { setAdding(false); setStatusMode(null); }} style={{ padding: '2px 8px', border: 'none', background: 'var(--border)', color: 'var(--text-secondary)', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>×</button>
          </form>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            <button 
              onClick={() => setAdding(true)} 
              style={{ 
                border: '1px dashed var(--accent)', background: 'rgba(255, 112, 67, 0.04)', 
                color: 'var(--accent-dark)', fontSize: 11, padding: '4px 8px', borderRadius: 6, 
                cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' 
              }}
            >
              + Comment
            </button>
            {isVendorType && (
              <>
                <button 
                  onClick={handleApprove} 
                  style={{ 
                    border: '1px solid #10B981', background: '#EBFDF5', 
                    color: '#047857', fontSize: 11, padding: '4px 8px', borderRadius: 6, 
                    cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, transition: 'all 0.15s' 
                  }}
                >
                  ✓ Approve
                </button>
                <button 
                  onClick={handleRejectClick} 
                  style={{ 
                    border: '1px solid #EF4444', background: '#FEF2F2', 
                    color: '#B91C1C', fontSize: 11, padding: '4px 8px', borderRadius: 6, 
                    cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, transition: 'all 0.15s' 
                  }}
                >
                  ✗ Reject
                </button>
              </>
            )}
          </div>
        )
      )}
    </div>
  );
}

// ── Excel viewer ──────────────────────────────────────────────────────────────
function ExcelViewer({ data, reportName, fileId }) {
  const { user } = useAuthStore();
  const isAdminOrManager = ['ADMIN', 'MANAGER'].includes(user?.role);
  const [remarks, setRemarks] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkText, setBulkText] = useState('');
  const [rowVisibility, setRowVisibility] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [search, setSearch]           = useState('');
  const [sortCol, setSortCol]         = useState(null);
  const [sortDir, setSortDir]         = useState('asc');
  const [page, setPage]               = useState(1);
  const [showSheetsFlyout, setShowSheetsFlyout] = useState(data?.sheets?.length > 1);
  const [viewMode, setViewMode]       = useState('analytics'); // 'table' or 'analytics'
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyPendingAction, setShowOnlyPendingAction] = useState(false);
  const [colFilters, setColFilters]   = useState({});
  const [pageSize, setPageSize]       = useState(50);

  useEffect(() => {
    setSelectedRows(new Set());
    setBulkText('');
  }, [activeSheet]);

  useEffect(() => {
    if (data?.visibility) {
      setRowVisibility(data.visibility);
    }
  }, [data]);

  useEffect(() => {
    if (!fileId) return;
    const fetchRemarks = async () => {
      try {
        const res = await adminApi.getFileRemarks(fileId);
        setRemarks(res.data.remarks || []);
      } catch (e) {
        console.warn("Failed to load remarks:", e);
      }
    };
    fetchRemarks();
  }, [fileId]);

  const handleAddRemark = async (sheetName, rowKey, type, remarkText, status) => {
    try {
      const res = await adminApi.addFileRemark(fileId, {
        sheetName,
        rowKey,
        type,
        remark: remarkText,
        status
      });
      setRemarks(prev => [...prev, res.data.remark]);
      toast.success("Remark added");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to add remark");
    }
  };

  const handleBulkAction = async (status, remarkText) => {
    const finalRemark = remarkText?.trim() || (status === 'APPROVED' ? 'Approved' : '');
    if (!finalRemark) return;
    try {
      const keysArray = Array.from(selectedRows);
      const res = await adminApi.addFileRemarksBulk(fileId, {
        sheetName: sheet.name,
        rowKeys: keysArray,
        type: 'VENDOR',
        status: status || null,
        remark: finalRemark
      });
      setRemarks(prev => [...prev, ...res.data.remarks]);
      setSelectedRows(new Set());
      setBulkText('');
      toast.success(`Applied bulk action to ${keysArray.length} rows`);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to perform bulk action");
    }
  };

  const handleBulkVisibility = async (hiddenVal) => {
    let proceedHidden = hiddenVal;
    if (!hiddenVal) {
      const makeVisibleToClient = window.confirm(
        "Do you want to make these rows visible to client users?\n\n" +
        "Click OK (Yes) to make them visible to clients.\n" +
        "Click Cancel (No) to keep them visible only to internal users."
      );
      if (makeVisibleToClient) {
        proceedHidden = false;
      } else {
        proceedHidden = true;
        toast("Rows will remain visible only to internal users.");
        return;
      }
    }

    try {
      const keysArray = Array.from(selectedRows);
      const res = await adminApi.updateFileRowVisibility(fileId, {
        sheetName: sheet.name,
        rowKeys: keysArray,
        hidden: proceedHidden
      });
      setRowVisibility(res.data.visibility || []);
      setSelectedRows(new Set());
      toast.success(proceedHidden ? `Hidden ${keysArray.length} rows from clients` : `Unhidden ${keysArray.length} rows (visible to clients)`);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update row visibility");
    }
  };

  const handleSingleVisibility = async (rowKey, hiddenVal) => {
    let proceedHidden = hiddenVal;
    if (!hiddenVal) {
      const makeVisibleToClient = window.confirm(
        "Do you want to make this row visible to client users?\n\n" +
        "Click OK (Yes) to make it visible to clients.\n" +
        "Click Cancel (No) to keep it visible only to internal users."
      );
      if (makeVisibleToClient) {
        proceedHidden = false;
      } else {
        proceedHidden = true;
        toast("Row will remain visible only to internal users.");
        return;
      }
    }

    try {
      const res = await adminApi.updateFileRowVisibility(fileId, {
        sheetName: sheet.name,
        rowKeys: [rowKey],
        hidden: proceedHidden
      });
      setRowVisibility(res.data.visibility || []);
      toast.success(proceedHidden ? "Row hidden from clients" : "Row visible to clients");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update row visibility");
    }
  };

  const isRowHidden = (sheetName, rowKey) => {
    return rowVisibility.some(v => v.sheetName === sheetName && v.rowKey === rowKey && v.hidden);
  };

  const toggleRowSelection = (rowKey) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleSelectAllPage = (pageRows, currentSheet) => {
    const keys = pageRows.map((r, idx) => getRowKey(r, currentSheet.headers, (page-1)*pageSize+idx));
    setSelectedRows(prev => {
      const next = new Set(prev);
      const allSelected = keys.every(k => next.has(k));
      if (allSelected) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
  };

  const getRowKey = (row, headers, index) => {
    const keyHeaders = ['rma #', 'rma no', 'rma number', 'rma', 'serial #', 'serial no', 'serial number', 'serial'];
    for (const kh of keyHeaders) {
      const headerIdx = headers.findIndex(h => h.toLowerCase().trim() === kh);
      if (headerIdx !== -1 && row[headerIdx] !== undefined && row[headerIdx] !== null && String(row[headerIdx]).trim() !== '') {
        return String(row[headerIdx]).trim();
      }
    }
    return `Row-${index}`;
  };
  
  const sheet = data.sheets?.[activeSheet];
  useEffect(() => { setPage(1); setSearch(''); setSortCol(null); setViewMode('analytics'); setColFilters({}); setShowFilters(false); }, [activeSheet]);
  useEffect(() => { setPage(1); }, [search, colFilters]);

  const filteredRows = useMemo(() => {
    if (!sheet) return [];
    let rows = [...sheet.rows];

    // Filter out client-hidden rows
    if (!isAdminOrManager) {
      rows = rows.filter((row, idx) => {
        const key = getRowKey(row, sheet.headers, idx);
        return !rowVisibility.some(v => v.rowKey === key && v.hidden);
      });
    }

    // Apply "Action Needed Only" filter
    if (showOnlyPendingAction) {
      const resolvedVendorStatusKeys = new Set(
        remarks
          .filter(r => r.sheetName === sheet.name && r.type === 'VENDOR' && (r.status === 'APPROVED' || r.status === 'REJECTED'))
          .map(r => r.rowKey)
      );
      rows = rows.filter((row, idx) => {
        const key = getRowKey(row, sheet.headers, idx);
        return !resolvedVendorStatusKeys.has(key);
      });
    }

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r => r.some(c => String(c).toLowerCase().includes(q)));
    }
    if (Object.keys(colFilters).length > 0) {
      rows = rows.filter(r => {
        return Object.entries(colFilters).every(([ci, val]) => {
          if (!val) return true;
          return String(r[ci]).toLowerCase().includes(val.toLowerCase());
        });
      });
    }
    if (sortCol !== null) {
      const ct = sheet.colTypes?.[sortCol];
      rows.sort((a, b) => {
        if (ct === 'number' || ct === 'currency') {
          const an = parseFloat(String(a[sortCol]).replace(/[₹$,]/g,'')) || 0;
          const bn = parseFloat(String(b[sortCol]).replace(/[₹$,]/g,'')) || 0;
          return sortDir === 'asc' ? an - bn : bn - an;
        }
        return sortDir === 'asc'
          ? String(a[sortCol]).localeCompare(String(b[sortCol]))
          : String(b[sortCol]).localeCompare(String(a[sortCol]));
      });
    }
    return rows;
  }, [sheet, search, colFilters, sortCol, sortDir]);

  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const pageRows   = filteredRows.slice((page-1)*pageSize, page*pageSize);
  const handleSort = ci => { if (sortCol===ci) setSortDir(d=>d==='asc'?'desc':'asc'); else { setSortCol(ci); setSortDir('asc'); } };

  if (!data.sheets?.length) return (
    <div className="empty-state" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
      <h3>Empty workbook</h3><p>No data found in this file.</p>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden'}}>

      {/* Toolbar */}
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 12px',background:'var(--bg-card)',borderBottom:'1px solid var(--border)',flexWrap:'wrap',flexShrink:0}}>
        {data.sheets?.length > 0 && (
          <button className="btn btn-secondary" style={{gap:6, padding:'6px 12px', flexShrink:0}} onClick={() => setShowSheetsFlyout(true)}>
            <Icon.Sheet />
            <span>Sheets ({data.sheets.length})</span>
          </button>
        )}
        
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <div style={{position:'relative', width:240}}>
            <Icon.Search style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', opacity:0.4, width:14, height:14}}/>
            <input 
              type="text" 
              placeholder={`Search ${sheet?.rowCount??0} rows...`} 
              value={search}
              onChange={e=>setSearch(e.target.value)}
              style={{width:'100%', padding:'8px 12px 8px 34px', fontSize:13, borderRadius:20, border:'1px solid var(--border)', background:'var(--bg)', color:'var(--text-primary)', outline:'none'}}
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} style={{display:'flex', alignItems:'center', gap:6, padding:'8px 14px', fontSize:13, fontWeight:500, background:showFilters?'var(--accent-light)':'var(--bg)', color:showFilters?'var(--accent-dark)':'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:20, cursor:'pointer', transition:'all 0.2s'}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
            Filter
          </button>
          <button 
            onClick={() => setShowOnlyPendingAction(!showOnlyPendingAction)} 
            style={{
              display:'flex', alignItems:'center', gap:6, padding:'8px 14px', fontSize:13, fontWeight:700, 
              background:showOnlyPendingAction?'rgba(255, 112, 67, 0.1)':'var(--bg)', 
              color:showOnlyPendingAction?'var(--accent)':'var(--text-secondary)', 
              border:showOnlyPendingAction?'1px solid var(--accent)':'1px solid var(--border)', 
              borderRadius:20, cursor:'pointer', transition:'all 0.2s'
            }}
            title="Show only rows pending Approved/Rejected vendor actions"
          >
            ⚡ {showOnlyPendingAction ? 'Showing Actions Needed' : 'Action Needed Only'}
          </button>
        </div>

        <div style={{display:'flex', background:'var(--bg)', borderRadius:20, border:'1px solid var(--border)', overflow:'hidden', flexShrink:0}}>
          <button onClick={() => setViewMode('analytics')} style={{padding:'6px 12px', fontSize:13, fontWeight:500, cursor:'pointer', border:'none', background:viewMode==='analytics'?'var(--accent-light)':'transparent', color:viewMode==='analytics'?'var(--accent-dark)':'var(--text-secondary)', transition:'all 0.2s'}}>Analytics</button>
          <button onClick={() => setViewMode('table')} style={{padding:'6px 12px', fontSize:13, fontWeight:500, cursor:'pointer', border:'none', background:viewMode==='table'?'var(--accent-light)':'transparent', color:viewMode==='table'?'var(--accent-dark)':'var(--text-secondary)', transition:'all 0.2s'}}>Table</button>
        </div>

        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('kyro-open-with-query', {
              detail: { query: `drill down on ${reportName || 'this report'}` }
            }));
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 800,
            background: 'linear-gradient(135deg, rgba(0, 192, 250, 0.15) 0%, rgba(0, 82, 204, 0.15) 100%)',
            color: '#00C0FA',
            border: '1px solid rgba(0, 192, 250, 0.4)',
            borderRadius: 20,
            cursor: 'pointer',
            transition: 'all 0.2s',
            flexShrink: 0,
          }}
          title="Ask KYRO for multi-tier report drilldown and SLA insights"
        >
          <span style={{ fontSize: '13px' }}>🎙️</span>
          <span>Ask KYRO Co-Pilot</span>
        </button>

        {totalPages > 1 && (
          <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:4}}>
            <button className="btn btn-secondary" style={{padding:'5px 8px'}} disabled={page===1} onClick={()=>setPage(p=>p-1)}>‹</button>
            <span style={{fontSize:12,color:'var(--text-secondary)',minWidth:56,textAlign:'center'}}>{page}/{totalPages}</span>
            <button className="btn btn-secondary" style={{padding:'5px 8px'}} disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>›</button>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{display:'flex', flex:1, minHeight:0, position:'relative'}}>
        {viewMode === 'analytics' ? (
          <ReportAnalytics sheet={{ ...sheet, rows: filteredRows }} remarks={remarks} />
        ) : (
          <div className="table-container table-fade-in" style={{flex:1, overflow:'hidden', background:'var(--bg)', position:'relative', padding:'24px', display:'flex', flexDirection:'column'}}>
            <div className="table-card-wrapper" style={{flex:1, overflow:'auto'}}>
              <table className="data-table excel-table">
              <thead>
                <tr>
                  <th className="row-num-th" style={{ width: 40, textAlign: 'center' }}>
                    <input 
                      type="checkbox" 
                      checked={pageRows.length > 0 && pageRows.every(r => selectedRows.has(getRowKey(r, sheet.headers, (page-1)*pageSize + pageRows.indexOf(r))))}
                      onChange={() => handleSelectAllPage(pageRows, sheet)}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  {sheet?.headers.map((h,ci) => (
                    <th key={ci} onClick={()=>handleSort(ci)}>
                      <div style={{display:'flex',alignItems:'center',gap:4}}>
                        <span>{h}</span>
                        {sortCol===ci ? <span style={{color:'var(--accent)',fontSize:10}}>{sortDir==='asc'?'▲':'▼'}</span> : <Icon.Sort style={{opacity:0.25, width:12, height:12}}/>}
                      </div>
                    </th>
                  ))}
                  <th style={{ minWidth: 350, textAlign: 'left' }}>Kaizen Remarks</th>
                  <th style={{ minWidth: 350, textAlign: 'left' }}>Vendor Remarks</th>
                </tr>
                {showFilters && (
                  <tr>
                    <th className="row-num-th" style={{background:'rgba(241, 245, 249, 0.85)', borderBottom:'1px solid var(--border)'}}></th>
                    {sheet?.headers.map((h,ci) => (
                      <th key={`filter-${ci}`} style={{background:'rgba(248, 250, 252, 0.85)', backdropFilter:'blur(12px)', borderBottom:'1px solid var(--border)', padding:'6px 8px'}}>
                        <input
                          type="text"
                          placeholder={`Filter ${h}...`}
                          value={colFilters[ci] || ''}
                          onChange={e => setColFilters(prev => ({...prev, [ci]: e.target.value}))}
                          onClick={e => e.stopPropagation()}
                          style={{width:'100%', padding:'6px 12px', fontSize:12, borderRadius:99, border:'1px solid var(--border)', background:'white', outline:'none', boxShadow:'var(--shadow-sm)', transition:'all 0.2s'}}
                          onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 112, 67, 0.15)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'var(--shadow-sm)'; }}
                        />
                      </th>
                    ))}
                    <th style={{background:'rgba(248, 250, 252, 0.85)', borderBottom:'1px solid var(--border)'}}></th>
                    <th style={{background:'rgba(248, 250, 252, 0.85)', borderBottom:'1px solid var(--border)'}}></th>
                  </tr>
                )}
              </thead>
              <tbody>
                {pageRows.map((row,ri)=>{
                  const rKey = getRowKey(row, sheet.headers, (page-1)*pageSize+ri);
                  const isSelected = selectedRows.has(rKey);
                  const isHidden = isRowHidden(sheet.name, rKey);
                  const isInternal = ['ADMIN', 'MANAGER'].includes(user?.role);

                  const rowStyle = {
                    background: isSelected 
                      ? 'rgba(255, 112, 67, 0.04)' 
                      : (isHidden ? '#F8FAFC' : undefined),
                    opacity: isHidden ? 0.7 : 1,
                    transition: 'all 0.15s'
                  };

                  return (
                    <tr key={ri} style={rowStyle}>
                      <td className="row-num" style={{ textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => toggleRowSelection(rKey)}
                            style={{ cursor: 'pointer' }}
                          />
                          {isInternal && (
                            <button 
                              onClick={() => handleSingleVisibility(rKey, !isHidden)} 
                              title={isHidden ? "Hidden from clients. Click to unhide." : "Visible to clients. Click to hide."}
                              style={{ 
                                background: 'transparent', border: 'none', cursor: 'pointer', 
                                padding: '2px', display: 'flex', alignItems: 'center',
                                filter: isHidden ? 'grayscale(0)' : 'opacity(0.35) grayscale(100%)',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={e => e.currentTarget.style.filter = 'opacity(1)'}
                              onMouseLeave={e => e.currentTarget.style.filter = isHidden ? 'grayscale(0)' : 'opacity(0.35) grayscale(100%)'}
                            >
                              {isHidden ? '🔒' : '🔓'}
                            </button>
                          )}
                          {isHidden && <span style={{ fontSize: 9, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FCA5A5', padding: '1px 4px', borderRadius: 4, fontWeight: 700 }}>Internal</span>}
                        </div>
                      </td>
                      {row.map((val,ci)=>{ const {display,cls}=fmtCell(val,sheet.colTypes?.[ci]); return <td key={ci} className={`excel-cell ${cls}`}>{display}</td>; })}
                      <td className="excel-cell" style={{ verticalAlign: 'top', textAlign: 'left', padding: '8px 12px' }}>
                        <RemarksCell 
                          remarks={remarks.filter(rem => rem.sheetName === sheet.name && rem.rowKey === rKey && rem.type === 'KAIZEN')}
                          onAdd={(text, status) => handleAddRemark(sheet.name, rKey, 'KAIZEN', text, status)}
                          userRole={user?.role}
                          allowedRoles={['ADMIN', 'MANAGER']}
                        />
                      </td>
                      <td className="excel-cell" style={{ verticalAlign: 'top', textAlign: 'left', padding: '8px 12px' }}>
                        <RemarksCell 
                          remarks={remarks.filter(rem => rem.sheetName === sheet.name && rem.rowKey === rKey && rem.type === 'VENDOR')}
                          onAdd={(text, status) => handleAddRemark(sheet.name, rKey, 'VENDOR', text, status)}
                          userRole={user?.role}
                          allowedRoles={['CLIENT', 'VIEWER', 'ADMIN', 'MANAGER']}
                          isVendorType
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Bulk Action Right Drawer Flyout */}
            {selectedRows.size > 0 && (
              <div className="slide-in-right" style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
                background: 'var(--bg-card)', borderLeft: '1px solid var(--border)', 
                boxShadow: '-10px 0 30px rgba(0,0,0,0.15)', zIndex: 999, 
                display: 'flex', flexDirection: 'column', padding: '24px',
                boxSizing: 'border-box'
              }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Bulk Actions</h3>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                        {selectedRows.size} row{selectedRows.size > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <button 
                      onClick={() => { setSelectedRows(new Set()); setBulkText(''); }}
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 20, padding: 4 }}
                    >
                      ×
                    </button>
                  </div>

                  {/* Body Content */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        Add Remarks / Reason
                      </label>
                      <textarea 
                        value={bulkText}
                        onChange={e => setBulkText(e.target.value)}
                        placeholder="Type comment or rejection reason..."
                        rows={4}
                        style={{ 
                          width: '100%', padding: '10px 14px', fontSize: 13, border: '1px solid var(--border)', 
                          borderRadius: 8, outline: 'none', background: 'white', color: '#1E293B', resize: 'vertical',
                          boxSizing: 'border-box',
                          boxShadow: 'var(--shadow-sm)', transition: 'border-color 0.2s'
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        Status Operations
                      </label>
                      
                      <button 
                        onClick={() => handleBulkAction('APPROVED', bulkText || 'Approved')} 
                        className="btn" 
                        style={{ width: '100%', background: '#10B981', color: 'white', padding: '10px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', border: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                      >
                        ✓ Bulk Approve
                      </button>
                      
                      <button 
                        onClick={() => handleBulkAction('REJECTED', bulkText)} 
                        disabled={!bulkText.trim()}
                        className="btn" 
                        style={{ width: '100%', background: '#EF4444', color: 'white', padding: '10px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', border: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: bulkText.trim() ? 1 : 0.5 }}
                      >
                        ✗ Bulk Reject
                      </button>

                      <button 
                        onClick={() => handleBulkAction(null, bulkText)} 
                        disabled={!bulkText.trim()}
                        className="btn" 
                        style={{ width: '100%', background: 'white', color: 'var(--text-primary)', padding: '10px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', border: '1px solid var(--border)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: bulkText.trim() ? 1 : 0.5, boxShadow: 'var(--shadow-sm)' }}
                      >
                        💬 Bulk Comment
                      </button>
                    </div>

                    {['ADMIN', 'MANAGER'].includes(user?.role) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                          Internal Row Visibility
                        </label>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button 
                            onClick={() => handleBulkVisibility(true)} 
                            className="btn" 
                            style={{ flex: 1, background: '#475569', color: 'white', padding: '10px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', border: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                          >
                            🔒 Hide Selected
                          </button>
                          <button 
                            onClick={() => handleBulkVisibility(false)} 
                            className="btn" 
                            style={{ flex: 1, background: '#64748B', color: 'white', padding: '10px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', border: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                          >
                            🔓 Unhide Selected
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer actions */}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                    <button 
                      onClick={() => { setSelectedRows(new Set()); setBulkText(''); }} 
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
            )}
            </div>
            
            {/* Pagination */}
            <div className="pagination-bar">
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <span>Rows per page:</span>
                <select className="page-size-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  {[25, 50, 100, 200].map(sz => <option key={sz} value={sz}>{sz}</option>)}
                </select>
              </div>
              <div className="pagination-controls">
                <span>{(page-1)*pageSize + 1} - {Math.min(page*pageSize, filteredRows.length)} of {filteredRows.length}</span>
                <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <button className="page-btn" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{padding:'7px 14px',background:'var(--bg-card)',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,fontSize:11,color:'var(--text-muted)',flexShrink:0,flexWrap:'wrap'}}>
        <span>📊 {sheet?.headers?.length??0} cols</span>
        <span>·</span>
        <span>📋 {sheet?.rowCount??0} rows</span>
        {search && <><span>·</span><span>🔍 {filteredRows.length} matching</span></>}
        {data.parsedAt && <span style={{marginLeft:'auto'}}>{formatDistanceToNow(new Date(data.parsedAt),{addSuffix:true})}</span>}
      </div>

      {/* Right side flyout for Sheets */}
      {showSheetsFlyout && (
        <>
          <div style={{position:'fixed', inset:0, zIndex: 998, background: 'rgba(0,0,0,0.2)'}} onClick={() => setShowSheetsFlyout(false)}></div>
          <div style={{
            position:'fixed', top:0, right:0, bottom:0, width: 320, background: 'var(--bg-card)',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.1)', zIndex: 999, display: 'flex', flexDirection: 'column',
            animation: 'slideIn 0.2s ease-out'
          }}>
            <div style={{padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <div style={{fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-display)', color: 'var(--text-primary)'}}>Workbook Sheets</div>
              <button onClick={() => setShowSheetsFlyout(false)} style={{background:'transparent', border:'none', cursor:'pointer', fontSize: 24, lineHeight: 1, color: 'var(--text-secondary)'}}>&times;</button>
            </div>
            <div style={{flex: 1, overflowY: 'auto', padding: '16px'}}>
              {data.sheets.map((s, i) => (
                <button key={i} onClick={() => { setActiveSheet(i); setShowSheetsFlyout(false); }} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '14px 16px', background: activeSheet === i ? 'var(--accent-light)' : 'var(--bg)',
                  border: '1px solid', borderColor: activeSheet === i ? 'var(--accent)' : 'var(--border)',
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left', marginBottom: 10,
                  color: activeSheet === i ? 'var(--accent-dark)' : 'var(--text-primary)',
                  transition: 'all 0.15s'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden'}}>
                    <div style={{flexShrink: 0}}><Icon.Sheet /></div>
                    <span style={{fontWeight: activeSheet === i ? 700 : 500, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={s.name}>{s.name}</span>
                  </div>
                  {s.rowCount > 0 && <span style={{fontSize: 11, background: activeSheet === i ? 'white' : 'white', padding: '2px 8px', borderRadius: 99, color: 'var(--text-secondary)', fontWeight: 600, border: '1px solid var(--border)', flexShrink: 0}}>{s.rowCount} rows</span>}
                </button>
              ))}
            </div>
          </div>
          <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        </>
      )}
    </div>
  );
}

// ── PDF viewer ────────────────────────────────────────────────────────────────
function PdfViewer({ streamUrl, name }) {
  const [zoom, setZoom]   = useState(100);
  const [error, setError] = useState(false);

  if (error) return (
    <div className="empty-state" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      <h3>PDF could not be displayed</h3>
      <p>The file may still be syncing from OneDrive. Try refreshing.</p>
    </div>
  );

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:'#525659'}}>
      {/* PDF controls */}
      <div style={{display:'flex',alignItems:'center',gap:6,padding:'7px 12px',background:'#3D4042',borderBottom:'1px solid rgba(0,0,0,0.3)',flexShrink:0}}>
        <span style={{fontSize:12,color:'rgba(255,255,255,0.6)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</span>
        <button onClick={()=>setZoom(z=>Math.max(z-20,50))} title="Zoom out" style={{border:'none',background:'rgba(255,255,255,0.1)',cursor:'pointer',padding:'5px 8px',borderRadius:4,color:'white',display:'flex',alignItems:'center'}}><Icon.ZoomOut/></button>
        <span style={{fontSize:12,color:'rgba(255,255,255,0.7)',minWidth:38,textAlign:'center'}}>{zoom}%</span>
        <button onClick={()=>setZoom(z=>Math.min(z+20,200))} title="Zoom in" style={{border:'none',background:'rgba(255,255,255,0.1)',cursor:'pointer',padding:'5px 8px',borderRadius:4,color:'white',display:'flex',alignItems:'center'}}><Icon.ZoomIn/></button>
        <button onClick={()=>setZoom(100)} title="Fit" style={{border:'none',background:'rgba(255,255,255,0.1)',cursor:'pointer',padding:'5px 12px',borderRadius:4,color:'rgba(255,255,255,0.7)',fontSize:11}}>Fit</button>
      </div>
      {/* PDF render — uses flex so it fills available height correctly on all devices */}
      <div style={{flex:1,overflow:'auto',WebkitOverflowScrolling:'touch',display:'flex',justifyContent:'center',padding:'16px 12px'}}>
        <div style={{width:`${zoom}%`,maxWidth:1000,minWidth:240,transition:'width 0.2s'}}>
          <object
            data={streamUrl}
            type="application/pdf"
            style={{width:'100%',height:'100%',minHeight:'70vh',border:'none',borderRadius:4,boxShadow:'0 4px 24px rgba(0,0,0,0.4)',display:'block'}}
            onError={()=>setError(true)}
          >
            <iframe src={streamUrl} style={{width:'100%',minHeight:'70vh',border:'none'}} title={name} onError={()=>setError(true)}/>
          </object>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReportViewerPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { logout } = useAuthStore();
  const [report, setReport]         = useState(null);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const load = async (refresh=false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await reportsApi.getData(id, refresh);
      setReport(res.data.report);
      setData(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load report');
      navigate('/');
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',flexDirection:'column',gap:12}}>
      <div className="spinner"/>
      <span style={{fontSize:13,color:'var(--text-secondary)'}}>Loading report…</span>
    </div>
  );

  const emoji   = FILE_EMOJI[report?.fileType?.toLowerCase()] ?? '📁';
  const isExcel = ['xlsx','xls','xlsm','csv'].includes((report?.fileType??'').toLowerCase());
  const isPdf   = report?.fileType?.toLowerCase() === 'pdf';
  const pdfUrl  = isPdf ? reportsApi.pdfStreamUrl(id) : null;

  return (
    /* Full-height flex column — works on desktop and mobile */
    <div style={{height:'100dvh',display:'flex',flexDirection:'column',overflow:'hidden',background:'var(--bg)'}}>

      {/* ── Mobile top bar (hamburger + logo) ─────────────────────────────── */}
      <div className="mobile-topbar" style={{flexShrink:0}}>
        <button className="mobile-menu-btn" onClick={()=>setMobileMenuOpen(true)}>
          <Icon.Menu/>
        </button>
        <img src="/logo.png" alt="PX-Reports" style={{height:26}}/>
        <div style={{width:38}}/>
      </div>

      {/* Mobile nav overlay */}
      {mobileMenuOpen && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:998}} onClick={()=>setMobileMenuOpen(false)}>
          <div style={{background:'var(--bg-sidebar)',width:270,height:'100%',padding:'20px 0',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'0 16px 16px',borderBottom:'1px solid rgba(255,255,255,0.08)',marginBottom:12}}>
              <img src="/logo.png" alt="PX-Reports" style={{height:32}}/>
            </div>
            <button onClick={()=>{navigate(-1);setMobileMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 18px',border:'none',background:'transparent',color:'rgba(255,255,255,0.75)',cursor:'pointer',fontSize:14,fontFamily:'var(--font)'}}>
              <Icon.Back/> Back to reports
            </button>
            <button onClick={()=>{navigate('/');setMobileMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 18px',border:'none',background:'transparent',color:'rgba(255,255,255,0.75)',cursor:'pointer',fontSize:14,fontFamily:'var(--font)'}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              All reports
            </button>
          </div>
        </div>
      )}

      {/* ── Report toolbar ────────────────────────────────────────────────── */}
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'var(--bg-card)',borderBottom:'1px solid var(--border)',flexShrink:0,flexWrap:'wrap'}}>

        {/* Back button — hidden on mobile (use hamburger instead) */}
        <button className="btn-icon" onClick={()=>navigate(-1)} title="Back" style={{flexShrink:0}} id="desktop-back">
          <Icon.Back/>
        </button>

        <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:0}}>
          <span style={{fontSize:20,flexShrink:0}}>{emoji}</span>
          <div style={{minWidth:0}}>
            <div style={{fontWeight:700,fontSize:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{report?.name}</div>
            <div style={{fontSize:11,color:'var(--text-muted)',display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
              <span>{report?.category}</span>
              <span>·</span>
              <span style={{padding:'1px 5px',borderRadius:99,background:isPdf?'#FEF2F2':'#F0FDF4',color:isPdf?'#DC2626':'#15803D',fontWeight:700,fontSize:10}}>
                {report?.fileType?.toUpperCase()}
              </span>
              <span>·</span>
              <span>{report?.sizeFormatted}</span>
              {report?.mappedFolderName && <><span>·</span><span style={{display:'inline-flex',alignItems:'center',gap:3}}>📁{report.mappedFolderName}</span></>}
            </div>
          </div>
        </div>

        <span className={`category-badge cat-${report?.category}`} style={{flexShrink:0}}>{report?.category}</span>

        {isExcel && (
          <button className="btn btn-secondary" style={{gap:5,flexShrink:0}} onClick={()=>load(true)} disabled={refreshing}>
            <Icon.Refresh/><span className="hide-xs">{refreshing?'Refreshing…':'Refresh'}</span>
          </button>
        )}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isExcel && data?.type==='excel' && <ExcelViewer data={data} reportName={report?.name} fileId={report?.onedriveId}/>}
      {isPdf   && pdfUrl              && <PdfViewer streamUrl={pdfUrl} name={report?.name}/>}
      {data?.type==='unsupported' && (
        <div className="empty-state" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <h3>Format not supported</h3>
          <p>Ask your admin to convert this report to Excel or PDF.</p>
        </div>
      )}
      {!data && !loading && (
        <div className="empty-state" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <h3>Report data unavailable</h3>
          <p>The report may not be synced from OneDrive yet.</p>
          <button className="btn btn-secondary" style={{marginTop:12}} onClick={()=>load(true)}><Icon.Refresh/> Retry</button>
        </div>
      )}
    </div>
  );
}
